/**
 * injuryStatus — one place that turns a raw `injury_status` from
 * getInjuryReport into a severity rank, a colour tier and labels.
 *
 * Statuses seen live (Oct 2026): out, ir, il_10-day, il_60-day, gtd, dtd,
 * questionable. Before this, `gtd` had no colour, `questionable` was labelled
 * "GTD", and IR/IL players had no severity rank, so "OUT only" hid them.
 *
 * Only shows what the feed carries: there is no return date, so the popover
 * says "Updated <date>" and nothing more.
 */

/** Colour tiers, most to least severe. Distinct hues so tiers read at a glance. */
export const INJURY_TIERS = {
    out:          { rank: 0, color: '#f87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.45)', name: 'Out' },
    doubtful:     { rank: 1, color: '#fb923c', bg: 'rgba(251,146,60,0.14)',  border: 'rgba(251,146,60,0.45)',  name: 'Doubtful' },
    questionable: { rank: 2, color: '#fbbf24', bg: 'rgba(251,191,36,0.13)',  border: 'rgba(251,191,36,0.42)',  name: 'Questionable' },
    daytoday:     { rank: 3, color: '#7dd3fc', bg: 'rgba(125,211,252,0.12)', border: 'rgba(125,211,252,0.38)', name: 'Day-to-day' },
    probable:     { rank: 5, color: '#4ade80', bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.38)',  name: 'Probable' },
    unknown:      { rank: 6, color: 'rgba(255,255,255,0.65)', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.18)', name: 'Listed' },
};

const SUSPENDED = { rank: 0, color: '#c4b5fd', bg: 'rgba(167,139,250,0.14)', border: 'rgba(167,139,250,0.45)', name: 'Suspended' };

/** Exact statuses → [tier, short label, long label]. */
const EXACT = {
    out: ['out', 'OUT', 'Out'],
    o: ['out', 'OUT', 'Out'],
    ir: ['out', 'IR', 'Injured reserve'],
    pup: ['out', 'PUP', 'Physically unable to perform'],
    nfi: ['out', 'NFI', 'Non-football injury list'],
    doubtful: ['doubtful', 'DOUBT', 'Doubtful'],
    d: ['doubtful', 'DOUBT', 'Doubtful'],
    questionable: ['questionable', 'Q', 'Questionable'],
    q: ['questionable', 'Q', 'Questionable'],
    gtd: ['questionable', 'GTD', 'Game-time decision'],
    'game-time decision': ['questionable', 'GTD', 'Game-time decision'],
    'day-to-day': ['daytoday', 'DTD', 'Day-to-day'],
    dtd: ['daytoday', 'DTD', 'Day-to-day'],
    probable: ['probable', 'PROB', 'Probable'],
    p: ['probable', 'PROB', 'Probable'],
};

/**
 * Describes a raw status: { key, rank, short, long, color, bg, border, tierName }.
 * Unknown statuses keep their own text (never relabelled as something else).
 */
export function describeInjuryStatus(raw) {
    const key = String(raw || '').trim().toLowerCase();
    if (key === 'suspended' || key === 'susp') {
        return { key, ...SUSPENDED, tierName: SUSPENDED.name, short: 'SUSP', long: 'Suspended' };
    }
    const exact = EXACT[key];
    if (exact) {
        const tier = INJURY_TIERS[exact[0]];
        return { key, ...tier, tierName: tier.name, short: exact[1], long: exact[2] };
    }
    // Injured list variants: il_10-day, il-60, IL15, "il_7-day" …
    const il = key.match(/^il[\s_-]*(\d+)/);
    if (il) {
        const tier = INJURY_TIERS.out;
        return { key, ...tier, tierName: tier.name, short: `IL-${il[1]}`, long: `${il[1]}-day injured list` };
    }
    const tier = INJURY_TIERS.unknown;
    const text = key ? key.replace(/[_-]+/g, ' ').toUpperCase().slice(0, 8) : '—';
    return { key, ...tier, tierName: tier.name, short: text, long: key ? key.replace(/[_-]+/g, ' ') : 'Status not given' };
}

/** Lower = more severe. Matches the settings filter thresholds below. */
export const severityRank = (raw) => describeInjuryStatus(raw).rank;

/** Settings → highest rank that is still shown. */
export const SEVERITY_THRESHOLD = {
    all: 99,
    questionable_up: 2,
    doubtful_up: 1,
    out_only: 0,
};

export function passesSeverity(raw, minSeverity) {
    const limit = SEVERITY_THRESHOLD[minSeverity] ?? 99;
    return severityRank(raw) <= limit;
}

/** Reads a timestamp in any shape the API returns (ISO, ms, {_seconds}, {seconds}). */
export function parseInjuryTimestamp(v) {
    if (v == null || v === '') return null;
    let d = null;
    if (typeof v === 'object' && !(v instanceof Date)) {
        const s = v._seconds ?? v.seconds;
        if (Number.isFinite(s)) d = new Date(s * 1000);
    } else {
        d = new Date(v);
    }
    return d && Number.isFinite(d.getTime()) ? d : null;
}

/**
 * The injury fields a popover can show, only the ones present:
 * { headline, lines: [..], updatedAt: Date|null }.
 */
export function injuryDetail(player) {
    const d = (player && player.injury_details) || {};
    const clean = (x) => (typeof x === 'string' ? x.trim() : '');
    const type = clean(d.injury_type);
    const bodyPart = clean(d.body_part);
    const side = clean(d.side);
    const designation = clean(d.designation);
    const description = clean(d.description);

    const where = [side, bodyPart].filter(Boolean).join(' ');
    let headline = type;
    if (where && !type.toLowerCase().includes(bodyPart.toLowerCase())) {
        headline = type ? `${type} · ${where}` : where;
    }
    if (!headline) headline = designation || '';
    const lines = [];
    if (designation && designation !== headline) lines.push(designation);
    if (description && description !== headline && description !== designation) lines.push(description);
    const updatedAt = parseInjuryTimestamp(d.updated_at) || parseInjuryTimestamp(player && player.injury_updated_at);
    return { headline, lines, updatedAt };
}

/** Short chip reason (the old "type — body part" line). */
export function injuryReason(player) {
    return injuryDetail(player).headline;
}

/** "Updated Oct 9, 3:12 PM" (adds the year when it differs from `now`). */
export function formatUpdated(date, now = new Date()) {
    if (!date) return '';
    const sameYear = date.getFullYear() === now.getFullYear();
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `Updated ${day}, ${time}`;
}
